import React, { useEffect, useState, useMemo } from 'react';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { Row, Col, Button as BootstrapButton, Card, Form, Alert } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Config } from '../config';
import useFirebase from '../hooks/useFirebase';
import { AskForConstants, EstimateStatus, Invoice, InvoiceStatus, PayingClient } from '../types/invoiceType';
import DisplayInvoice from './displayInvoice';
import AdvancedSectionsDisplay from './AdvancedSectionsDisplay';
import EstimateSignatureSection from './EstimateSignatureSection';
import Header from './header';
import '../App.css';
import { v4 as uuidv4 } from 'uuid';
import { getCollectionName } from '../utils/firebase';

/* Solar screen estimate component that allows customers to:
1. View solar screen estimates
2. Select mesh and frame colors
3. Accept the estimate
4. Pay a deposit (50% of total) 
*/
const Estimate = () => {
    const { estimateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const connectedAccountId = searchParams.get('connectedAccountId');
    const companyId = searchParams.get('companyId');
    const [invoice, setInvoice] = React.useState<Invoice | null>(null);
    const { db, analytics } = useFirebase();
    const [estimateAccepted, setEstimateAccepted] = React.useState(false);
    const [workItems, setWorkItems] = useState<any[]>([]);
    const [customer, setCustomer] = useState<PayingClient | null>(null);

    // Color selection state
    const [selectedMeshColor, setSelectedMeshColor] = useState<string>('');
    const [selectedFrameColor, setSelectedFrameColor] = useState<string>('');
    const [colorSelectionComplete, setColorSelectionComplete] = useState<boolean>(false);

    // Available color options
    const meshColors = ["Black", "Stucco", "Gray", "Beige", "White", "Brown"];
    const frameColors = ["Tan", "White", "Bronze", "Silver", "Champagne"];

    const [acceptSuccessMessage, setAcceptSuccessMessage] = useState<boolean>(false);
    const [depositURL, setDepositURL] = useState<string>('');
    const [totalDue, setTotalDue] = useState<number>();
    const [displayInvoiceRefreshKey, setDisplayInvoiceRefreshKey] = useState(0);

    // Payment plan state
    const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<string>('half_now');
    // Payment plan options with their descriptions
    const paymentPlans = [
        { id: 'months_12', name: '12 Monthly Payments', description: 'No interest, equal monthly payments', minAmount: 2500, discountPercent: 0 },
        { id: 'months_6', name: '6 Monthly Payments', description: 'No interest, equal monthly payments', minAmount: 0, discountPercent: 5 },
        { id: 'months_3', name: '3 Monthly Payments', description: 'No interest, equal monthly payments', minAmount: 0, discountPercent: 15 },
        { id: 'half_now', name: '50/50 Payment', description: '50% now, 50% after installation', minAmount: 0, discountPercent: 20 },
        { id: 'full_payment', name: 'Full Payment', description: 'Pay 100% now', minAmount: 0, discountPercent: 25 }
    ];

    // Get existing discount percentage from any available discount field (0-100 range)
    const getExistingDiscountAsPercent = (): number => {
        if (invoice?.discountPercent && invoice.discountPercent > 0) return invoice.discountPercent;
        if (invoice?.discount && invoice.discount > 0) return invoice.discount;
        if (invoice?.discountType === 'percentage' && invoice?.discountValue && invoice.discountValue > 0) {
            return invoice.discountValue;
        }
        if (invoice?.discountType === 'fixed' && invoice?.discountAmount && invoice.discountAmount > 0) {
            const baseAmount = invoice.subtotal || invoice.amount || 0;
            if (baseAmount > 0) return (invoice.discountAmount / baseAmount) * 100;
        }
        return 0;
    };
    const existingDiscountPercent = getExistingDiscountAsPercent();


    // Fetch estimate data from the estimates collection
    useEffect(() => {
        const fetchEstimate = async () => {
            if (!estimateId || !db) return;

            try {
                const docRef = doc(db, getCollectionName("estimates"), estimateId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const estimateData = docSnap.data();

                    // Extract work items from dimensions
                    if (estimateData.dimensions && Array.isArray(estimateData.dimensions)) {
                        console.log("estimateData.dimensions", estimateData.dimensions);
                        setWorkItems(estimateData.dimensions);
                    }

                    // Convert estimate data to Invoice format for compatibility with existing code
                    const invoiceFormat = {
                        ...estimateData,
                        id: estimateId,
                        isEstimate: true,
                        amount: estimateData.totalPrice || 0,
                        fullName: estimateData.customerName || '',
                    };

                    setInvoice(invoiceFormat as Invoice);

                    // Fetch customer data from clients collection using userId field
                    if (estimateData.userId) {
                        try {
                            const customerDocRef = doc(db, getCollectionName("clients"), estimateData.userId);
                            const customerSnap = await getDoc(customerDocRef);
                            if (customerSnap.exists()) {
                                setCustomer(customerSnap.data() as PayingClient);
                            }
                        } catch (error) {
                            console.error("Error fetching customer data:", error);
                        }
                        // Fallback to customerId if userId doesn't exist (for backward compatibility)
                    } else if (estimateData.customerId) {
                        try {
                            const customerDocRef = doc(db, getCollectionName("clients"), estimateData.customerId);
                            const customerSnap = await getDoc(customerDocRef);
                            if (customerSnap.exists()) {
                                setCustomer(customerSnap.data() as PayingClient);
                            }
                        } catch (error) {
                            console.error("Error fetching customer data:", error);
                        }
                    }

                    // Set selected colors if they exist in the estimate
                    if (estimateData.meshColor) {
                        setSelectedMeshColor(estimateData.meshColor);
                    }
                    if (estimateData.frameColor) {
                        setSelectedFrameColor(estimateData.frameColor);
                    }
                    if (estimateData.meshColor && estimateData.frameColor) {
                        setColorSelectionComplete(true);
                    }

                    if (estimateData.status === EstimateStatus.accepted || estimateData.status === EstimateStatus.signed) {
                        setEstimateAccepted(true);

                        // Load saved payment plan selection
                        if (estimateData.paymentPlan) {
                            setSelectedPaymentPlan(estimateData.paymentPlan);
                        }

                        // Set deposit URL if it exists
                        if (estimateData.depositLink) {
                            setDepositURL(estimateData.depositLink);
                        } else {
                            // Generate deposit link if needed, but don't call inside useEffect
                            // Instead, set a flag to generate it later
                            const timer = setTimeout(() => {
                                if (!depositURL) {
                                    generateDepositLink(invoiceFormat as Invoice);
                                }
                            }, 500);

                            // Clean up timer to avoid memory leaks
                            return () => clearTimeout(timer);
                        }
                    }
                } else {
                    console.error("No estimate found with ID:", estimateId);
                }
            } catch (error) {
                console.error("Error fetching estimate:", error);
            }
        };

        fetchEstimate();
        // Remove depositURL from dependencies to avoid infinite loops
    }, [estimateId, db]);

    // Function to calculate payment based on selected plan
    const calculatePayment = (amount: number, plan: string) => {
        // Only calculate payment plans for dimension-based pricing (solar screens) or undefined pricingType
        if (invoice?.pricingType && invoice.pricingType !== "dimensions") {
            return {
                initialPayment: amount,
                discount: 0,
                total: amount,
                term: 0
            };
        }

        // Get any existing discount percentage from the invoice (convert from 0-100 to 0-1)
        const existingDiscountPercent = getExistingDiscountAsPercent() / 100;

        switch (plan) {
            case 'months_12':
                return {
                    initialPayment: Math.round(amount * (1 - existingDiscountPercent) / 12),
                    discount: existingDiscountPercent,
                    total: Math.round(amount * (1 - existingDiscountPercent)),
                    term: 12
                };
            case 'months_6':
                // Give 5% discount for 6 month plan only if 12 month plan is available (amount >= 2500)
                const planDiscount6 = amount >= 2500 ? 0.05 : 0;
                // Combine existing discount with plan discount
                const totalDiscount6 = planDiscount6 + existingDiscountPercent - (planDiscount6 * existingDiscountPercent);
                return {
                    initialPayment: Math.round((amount * (1 - totalDiscount6)) / 6),
                    discount: totalDiscount6,
                    total: Math.round(amount * (1 - totalDiscount6)),
                    term: 6
                };
            case 'months_3':
                // Combine existing discount with 15% plan discount
                const planDiscount3 = 0.15;
                const totalDiscount3 = planDiscount3 + existingDiscountPercent - (planDiscount3 * existingDiscountPercent);
                return {
                    initialPayment: Math.round((amount * (1 - totalDiscount3)) / 3),
                    discount: totalDiscount3,
                    total: Math.round(amount * (1 - totalDiscount3)),
                    term: 3
                };
            case 'half_now':
                // Combine existing discount with 20% plan discount
                const planDiscount50 = 0.2;
                const totalDiscount50 = planDiscount50 + existingDiscountPercent - (planDiscount50 * existingDiscountPercent);
                const discountedTotal = Math.round(amount * (1 - totalDiscount50));
                const halfPayment = Math.round(discountedTotal / 2);

                return {
                    initialPayment: halfPayment, // 50% of discounted total
                    discount: totalDiscount50,
                    total: discountedTotal,
                    term: 0 // Pay half now, half on completion
                };
            case 'full_payment':
            default:
                // Combine existing discount with 25% plan discount
                const planDiscountFull = 0.25;
                const totalDiscountFull = planDiscountFull + existingDiscountPercent - (planDiscountFull * existingDiscountPercent);
                return {
                    initialPayment: Math.round(amount * (1 - totalDiscountFull)), // 100% upfront with combined discount
                    discount: totalDiscountFull,
                    total: Math.round(amount * (1 - totalDiscountFull)),
                    term: 0 // Pay in full
                };
        }
    };

    // Function to generate a payment link based on selected plan
    const generateDepositLink = async (invoiceData = invoice) => {
        if (!invoiceData) return;

        // Don't regenerate if depositURL is already set
        if (depositURL) return;

        try {
            // Calculate the total amount based on the sum of prices from selectedServices if available
            let totalAmount = invoiceData.amount;

            // If selectedServices exists, use the sum of its prices
            if (invoiceData.selectedServices) {
                const rawTotal = Object.values(invoiceData.selectedServices).reduce((sum, service: any) => {
                    // Safely extract price and ensure it's a number
                    const price = typeof service.price === 'number' ? service.price : 0;
                    return sum + price;
                }, 0) as number; // Explicitly cast the result to number

                if (rawTotal > 0) {
                    console.log(`Using raw total from selectedServices: ${rawTotal}`);
                    totalAmount = rawTotal;
                }
            }

            // Update the database first
            const docRef = doc(db, getCollectionName("estimates"), invoiceData.id);
            const updateData: any = {};

            // Only add payment plan fields if this is a dimension-based estimate
            if (!invoiceData.pricingType || invoiceData.pricingType === "dimensions") {
                // Calculate payment based on selected plan using the raw total
                const payment = calculatePayment(totalAmount, selectedPaymentPlan);

                // Create a deposit payment URL with the initial payment amount
                let newDepositURL = `/payment/${estimateId}?amount=${payment.initialPayment}&isDeposit=true&plan=${selectedPaymentPlan}`;
                if (connectedAccountId) {
                    newDepositURL += `&connectedAccountId=${connectedAccountId}`;
                } else if (companyId) {
                    newDepositURL += `&companyId=${companyId}`;
                }

                updateData.depositLink = newDepositURL;
                updateData.depositAmount = payment.initialPayment;
                updateData.paymentPlan = selectedPaymentPlan;
                updateData.discountedTotal = payment.total;
                updateData.discountPercent = payment.discount * 100;

                await updateDoc(docRef, updateData);

                // Update UI state after database update completes
                setDepositURL(newDepositURL);

                // Only update invoice if needed and in a way that doesn't trigger unnecessary re-renders
                if (invoiceData.depositLink !== newDepositURL) {
                    const updatedFields: any = {
                        depositLink: newDepositURL,
                        depositAmount: payment.initialPayment,
                        paymentPlan: selectedPaymentPlan as "months_12" | "months_6" | "months_3" | "half_now" | "full_payment" | "none",
                        discountedTotal: payment.total,
                        discountPercent: payment.discount * 100
                    };

                    // Use object spread to create a new object without triggering deep re-renders
                    setInvoice({ ...invoiceData, ...updatedFields });
                }
            } else {
                // Set paymentPlan to "none" for non-dimension-based estimates
                updateData.paymentPlan = "none";

                await updateDoc(docRef, updateData);

                // Update local state
                const updatedFields: any = {
                    paymentPlan: "none"
                };

                // Use object spread to create a new object without triggering deep re-renders
                setInvoice({ ...invoiceData, ...updatedFields });
            }

        } catch (error) {
            console.error("Error generating payment link:", error);
        }
    };

    useEffect(() => {
        if (!invoice) { return; }
        if (!selectedPaymentPlan) { return; }
        if (invoice.status === EstimateStatus.accepted || invoice.status === EstimateStatus.signed) {
            return;
        }

        console.log("[ESTIMATE] Estimate status:", invoice.status);
        const totalDue = calculatePayment(invoice.amount, selectedPaymentPlan).total;
        console.log("[ESTIMATE] Total due calculated:", totalDue);
        setTotalDue(totalDue);

    }, [selectedPaymentPlan, invoice]);

    const acceptEstimate = async () => {
        if (!invoice) { return; }

        // Only check for colors if dealing with solar screens
        const isSolarScreen = invoice.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
            service.type?.toLowerCase().includes('solar') ||
            service.name?.toLowerCase().includes('solar')
        );

        if (isSolarScreen && (!selectedMeshColor || !selectedFrameColor)) {
            alert('Please select both mesh and frame colors before accepting the estimate.');
            return;
        }

        try {
            // Calculate the total amount based on the sum of prices from selectedServices if available
            let totalAmount = invoice.amount;

            // If selectedServices exists, use the sum of its prices
            if (invoice.selectedServices) {
                const rawTotal = Object.values(invoice.selectedServices).reduce((sum, service: any) => {
                    // Safely extract price and ensure it's a number
                    const price = typeof service.price === 'number' ? service.price : 0;
                    return sum + price;
                }, 0) as number; // Explicitly cast the result to number

                if (rawTotal > 0) {
                    console.log(`Using raw total from selectedServices: ${rawTotal}`);
                    totalAmount = rawTotal;
                }
            }

            // Use estimates collection instead of invoices
            const docRef = doc(db, getCollectionName("estimates"), invoice.id);

            // Only create invoices for dimension-based pricing (solar screens) or undefined pricingType
            const invoiceIds = [];
            const dateCreated = new Date();

            if (!invoice.pricingType || invoice.pricingType === "dimensions") {
                // Calculate payment based on the selected plan using the raw total from selectedServices
                const payment = calculatePayment(totalAmount, selectedPaymentPlan);
                const depositURL = `/payment/${estimateId}?amount=${payment.initialPayment}&isDeposit=true&plan=${selectedPaymentPlan}`;

                // Determine number of invoices and payment amounts based on payment plan
                let numberOfInvoices = 1;
                let individualAmount = payment.total;

                switch (selectedPaymentPlan) {
                    case 'months_12':
                        numberOfInvoices = 12;
                        individualAmount = Math.round(payment.total / 12);
                        break;
                    case 'months_6':
                        numberOfInvoices = 6;
                        individualAmount = Math.round(payment.total / 6);
                        break;
                    case 'months_3':
                        numberOfInvoices = 3;
                        individualAmount = Math.round(payment.total / 3);
                        break;
                    case 'half_now':
                        numberOfInvoices = 2;
                        individualAmount = Math.round(payment.total / 2);
                        break;
                }

                // Create each invoice
                for (let i = 0; i < numberOfInvoices; i++) {
                    const invoiceId = `${invoice.id}_payment_${i + 1}`;
                    const isFirstPayment = i === 0;

                    const invoiceData: Invoice = {
                        id: invoiceId,
                        fullName: invoice.fullName,
                        address: invoice.address || '',
                        title: `${invoice.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                            service.type?.toLowerCase().includes('solar') ||
                            service.name?.toLowerCase().includes('solar')
                        ) ? "Solar Screen" : "Service"} Payment ${i + 1} of ${numberOfInvoices}`,
                        message: isFirstPayment
                            ? `This is your initial payment for your ${invoice.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                                service.type?.toLowerCase().includes('solar') ||
                                service.name?.toLowerCase().includes('solar')
                            ) ? "solar screen" : "service"} installation.`
                            : `This is payment ${i + 1} of ${numberOfInvoices} for your ${invoice.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                                service.type?.toLowerCase().includes('solar') ||
                                service.name?.toLowerCase().includes('solar')
                            ) ? "solar screen" : "service"} installation.`,
                        amount: individualAmount,
                        status: isFirstPayment ? 'pending' : 'scheduled',
                        askFor: AskForConstants.referral,
                        clientId: invoice.userId || invoice.customerId || uuidv4(),
                        isEstimate: false,
                        dateCreated: dateCreated,
                        meshColor: selectedMeshColor,
                        frameColor: selectedFrameColor,
                        depositAmount: isFirstPayment ? individualAmount : 0,
                        paymentPlan: selectedPaymentPlan as "months_12" | "months_6" | "months_3" | "half_now" | "full_payment",
                        parentEstimateId: invoice.id,
                        paymentNumber: i + 1,
                        totalPayments: numberOfInvoices,
                        discountPercent: payment.discount * 100,
                        discountedTotal: individualAmount * numberOfInvoices,
                        // Include line items from the estimate using selectedServices when available
                        lineItems: invoice.selectedServices
                            ? Object.entries(invoice.selectedServices).map(([key, service]) => {
                                // Use type assertion to handle the unknown type
                                const typedService = service as {
                                    dimensions?: {
                                        width?: { whole?: number; top?: string; bottom?: string };
                                        length?: { whole?: number; top?: string; bottom?: string };
                                    };
                                    price?: number;
                                    name?: string;
                                };

                                // Get dimensions
                                const dimensions = typedService.dimensions;
                                const width = dimensions?.width?.whole || 0;
                                const length = dimensions?.length?.whole || 0;

                                // Calculate area in square feet
                                const area = (width * length) / 144; // Convert from sq inches to sq feet

                                // Get service name
                                const serviceName = typedService.name || key;

                                // Get the raw price directly from the selectedServices data
                                // Divide by the number of invoices to distribute the price across payments
                                const rawPrice = typedService.price || 0;
                                const pricePerInvoice = Math.round(rawPrice / numberOfInvoices);

                                console.log(`Invoice Item: ${serviceName}, Raw Price: ${rawPrice}, Per Invoice: ${pricePerInvoice}`);

                                return {
                                    name: serviceName || `Solar Screen (${width}" × ${length}")`,
                                    description: `${area.toFixed(2)} sq ft, ${invoice.meshType || 90}% Solar Screen Mesh`,
                                    quantity: 1,
                                    unitPrice: pricePerInvoice
                                };
                            })
                            : workItems.map(item => {
                                const width = item.width?.whole || 0;
                                const length = item.length?.whole || 0;
                                const area = (width * length) / 144; // Convert from sq inches to sq feet

                                return {
                                    name: `Solar Screen (${width}" × ${length}")`,
                                    description: `${area.toFixed(2)} sq ft, ${invoice.meshType || 90}% Solar Screen Mesh`,
                                    quantity: 1,
                                    unitPrice: Math.round((individualAmount / workItems.length))
                                };
                            })
                    };

                    // Add the invoice to Firestore
                    await setDoc(doc(db, getCollectionName("invoices"), invoiceId), invoiceData);
                    invoiceIds.push(invoiceId);
                }

                // Update the estimate with accepted status, colors, payment plan details and linked invoices
                const updateData: any = {
                    status: EstimateStatus.accepted,
                    meshColor: selectedMeshColor,
                    frameColor: selectedFrameColor,
                    invoiceIds: invoiceIds,
                    firstInvoiceId: invoiceIds[0],
                    depositLink: depositURL,
                    depositAmount: payment.initialPayment,
                    paymentPlan: selectedPaymentPlan,
                    discountedTotal: payment.total,
                    discountPercent: payment.discount * 100
                };

                await updateDoc(docRef, updateData);

                // Update local state
                const updatedInvoice: Invoice = {
                    ...invoice,
                    status: EstimateStatus.accepted,
                    meshColor: selectedMeshColor,
                    frameColor: selectedFrameColor,
                    invoiceIds: invoiceIds,
                    firstInvoiceId: invoiceIds[0],
                    depositLink: depositURL,
                    depositAmount: payment.initialPayment,
                    paymentPlan: selectedPaymentPlan as "months_12" | "months_6" | "months_3" | "half_now" | "full_payment",
                    discountedTotal: payment.total,
                    discountPercent: payment.discount * 100
                };

                setInvoice(updatedInvoice);
                setDepositURL(depositURL);
            } else {
                // For non-dimension-based estimates, just update the estimate status without creating invoices
                const updateData: any = {
                    status: EstimateStatus.accepted,
                    paymentPlan: "none" as "none"
                };

                await updateDoc(docRef, updateData);

                // Update local state
                const updatedInvoice: Invoice = {
                    ...invoice,
                    status: EstimateStatus.accepted,
                    paymentPlan: "none"
                };

                setInvoice(updatedInvoice);
            }

            setEstimateAccepted(true);
            setColorSelectionComplete(true);
            setAcceptSuccessMessage(true);

            // Automatically scroll to the deposit section only for dimension-based estimates
            if (!invoice.pricingType || invoice.pricingType === "dimensions") {
                setTimeout(() => {
                    const depositSection = document.querySelector('.bg-success.bg-opacity-10');
                    if (depositSection) {
                        depositSection.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 500);
            }


        } catch (error) {
            console.error("Error accepting estimate:", error);
            alert('There was an error accepting the estimate. Please try again or contact us directly.');
        }
    };

    const updateColorSelections = async () => {
        if (!invoice || !selectedMeshColor || !selectedFrameColor) return;

        try {
            // Only update colors on the document - using estimates collection
            const docRef = doc(db, getCollectionName("estimates"), invoice.id);
            await updateDoc(docRef, {
                meshColor: selectedMeshColor,
                frameColor: selectedFrameColor
            });

            // Update local state
            setInvoice({
                ...invoice,
                meshColor: selectedMeshColor,
                frameColor: selectedFrameColor
            });

            setColorSelectionComplete(true);
        } catch (error) {
            console.error("Error updating color selections:", error);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4">
            <Header connectedAccountId={connectedAccountId} companyId={companyId} />

            <div className="my-4">
                {acceptSuccessMessage && (
                    <Alert variant="success" dismissible onClose={() => setAcceptSuccessMessage(false)} className="mb-4">
                        <strong>Estimate Accepted!</strong> Thank you for accepting the estimate.
                    </Alert>
                )}
                {invoice?.status === EstimateStatus.signed && (
                    <div className="bg-success bg-opacity-10 p-3 rounded-lg mb-4">
                        <h5 className="text-success m-0">
                            <i className="fas fa-file-signature me-2"></i>
                            Estimate Signed
                        </h5>
                        {invoice.signature && (
                            <p className="text-muted mb-0 mt-1">
                                Signed by <strong>{invoice.signature.signerFullName}</strong> on {new Date(invoice.signature.signedAtUTC).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                        )}
                    </div>
                )}
                {invoice?.status === EstimateStatus.accepted && (
                    <div className="bg-success bg-opacity-10 p-3 rounded-lg mb-4">
                        <h5 className="text-success m-0">
                            <i className="fas fa-check-circle me-2"></i>
                            Estimate Accepted
                        </h5>
                    </div>
                )}
            </div>

            {/* Solar Screen Color Selection - Only show for solar screens */}
            {invoice && (
                invoice.pricingType === "dimensions" ||
                (invoice.pricingType === undefined && (!invoice.selectedServices || Object.values(invoice.selectedServices).some((service: any) =>
                    service.type?.toLowerCase().includes('solar') ||
                    service.name?.toLowerCase().includes('solar')
                )))
            ) && (
                    <Card className="shadow mb-4">
                        <Card.Header className="bg-primary bg-opacity-10 py-3">
                            <h4 className="m-0">
                                <i className="fas fa-palette me-2"></i>
                                {invoice?.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                                    service.type?.toLowerCase().includes('solar') ||
                                    service.name?.toLowerCase().includes('solar')
                                ) ? "Solar Screen Colors" : "Colors"}
                            </h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="alert alert-primary mb-4">
                                <i className="fas fa-info-circle me-2"></i>
                                Please select your preferred colors for the {invoice?.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                                    service.type?.toLowerCase().includes('solar') ||
                                    service.name?.toLowerCase().includes('solar')
                                ) ? "solar screen mesh and frames" : "product"}. These selections will be saved with your order.
                            </div>

                            <Row className="mb-4">
                                <Col md={6} className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Header className="bg-light">
                                            <h5 className="m-0">Mesh Color</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="text-center mb-3">
                                                <img
                                                    src="/solar-screen-colors-scaled.jpg"
                                                    alt="Mesh Color Options"
                                                    className="img-fluid rounded mb-3"
                                                    style={{ maxHeight: "200px" }}
                                                />
                                                <div className="small text-muted mb-3">
                                                    Available mesh colors: Black, Stucco, Gray, Beige, White, Brown
                                                </div>
                                            </div>
                                            <Form.Group>
                                                <Form.Label><strong>Select Mesh Color:</strong></Form.Label>

                                                {/* Visual color selector */}
                                                <div className="d-flex flex-wrap mb-3">
                                                    {meshColors.map(color => (
                                                        <div
                                                            key={color}
                                                            onClick={() => {
                                                                if (!(estimateAccepted && colorSelectionComplete)) {
                                                                    setSelectedMeshColor(color);
                                                                }
                                                            }}
                                                            className={`color-swatch me-2 mb-2 ${selectedMeshColor === color ? 'selected' : ''}`}
                                                            style={{
                                                                cursor: (estimateAccepted && colorSelectionComplete) ? 'not-allowed' : 'pointer',
                                                                opacity: (estimateAccepted && colorSelectionComplete && selectedMeshColor !== color) ? 0.5 : 1,
                                                            }}
                                                        >
                                                            <div
                                                                className={`rounded p-3 border color-${color.toLowerCase()}`}
                                                                style={{
                                                                    width: "60px",
                                                                    height: "40px",
                                                                    position: "relative"
                                                                }}
                                                            >
                                                                {selectedMeshColor === color && (
                                                                    <div
                                                                        className="position-absolute"
                                                                        style={{
                                                                            top: "-8px",
                                                                            right: "-8px",
                                                                            backgroundColor: "#28a745",
                                                                            borderRadius: "50%",
                                                                            width: "22px",
                                                                            height: "22px",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            color: "white",
                                                                            fontSize: "12px"
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-check"></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-center small mt-1">{color}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Dropdown as fallback */}
                                                <Form.Control
                                                    as="select"
                                                    value={selectedMeshColor}
                                                    onChange={(e) => setSelectedMeshColor(e.target.value)}
                                                    disabled={estimateAccepted && colorSelectionComplete}
                                                    className="form-select py-2"
                                                >
                                                    <option value="">-- Select Mesh Color --</option>
                                                    {meshColors.map(color => (
                                                        <option key={color} value={color}>{color}</option>
                                                    ))}
                                                </Form.Control>
                                            </Form.Group>
                                        </Card.Body>
                                        {selectedMeshColor && (
                                            <Card.Footer className="bg-light">
                                                <div className="d-flex align-items-center">
                                                    <div className={`p-2 rounded-circle me-2 border color-${selectedMeshColor.toLowerCase()}`}
                                                        style={{
                                                            width: "24px",
                                                            height: "24px"
                                                        }}>
                                                    </div>
                                                    <span>Selected: <strong>{selectedMeshColor}</strong></span>
                                                </div>
                                            </Card.Footer>
                                        )}
                                    </Card>
                                </Col>

                                <Col md={6}>
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Header className="bg-light">
                                            <h5 className="m-0">Frame Color</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="text-center mb-3">
                                                <img
                                                    src="/screenframecolors-scaled.jpg"
                                                    alt="Frame Color Options"
                                                    className="img-fluid rounded mb-3"
                                                    style={{ maxHeight: "200px" }}
                                                />
                                                <div className="small text-muted mb-3">
                                                    Available frame colors: Tan, White, Bronze, Silver, Champagne
                                                </div>
                                            </div>
                                            <Form.Group>
                                                <Form.Label><strong>Select Frame Color:</strong></Form.Label>

                                                {/* Visual color selector */}
                                                <div className="d-flex flex-wrap mb-3">
                                                    {frameColors.map(color => (
                                                        <div
                                                            key={color}
                                                            onClick={() => {
                                                                if (!(estimateAccepted && colorSelectionComplete)) {
                                                                    setSelectedFrameColor(color);
                                                                }
                                                            }}
                                                            className={`color-swatch me-2 mb-2 ${selectedFrameColor === color ? 'selected' : ''}`}
                                                            style={{
                                                                cursor: (estimateAccepted && colorSelectionComplete) ? 'not-allowed' : 'pointer',
                                                                opacity: (estimateAccepted && colorSelectionComplete && selectedFrameColor !== color) ? 0.5 : 1,
                                                            }}
                                                        >
                                                            <div
                                                                className={`rounded p-3 border color-${color.toLowerCase()}`}
                                                                style={{
                                                                    width: "60px",
                                                                    height: "40px",
                                                                    position: "relative"
                                                                }}
                                                            >
                                                                {selectedFrameColor === color && (
                                                                    <div
                                                                        className="position-absolute"
                                                                        style={{
                                                                            top: "-8px",
                                                                            right: "-8px",
                                                                            backgroundColor: "#28a745",
                                                                            borderRadius: "50%",
                                                                            width: "22px",
                                                                            height: "22px",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            color: "white",
                                                                            fontSize: "12px"
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-check"></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-center small mt-1">{color}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Dropdown as fallback */}
                                                <Form.Control
                                                    as="select"
                                                    value={selectedFrameColor}
                                                    onChange={(e) => setSelectedFrameColor(e.target.value)}
                                                    disabled={estimateAccepted && colorSelectionComplete}
                                                    className="form-select py-2"
                                                >
                                                    <option value="">-- Select Frame Color --</option>
                                                    {frameColors.map(color => (
                                                        <option key={color} value={color}>{color}</option>
                                                    ))}
                                                </Form.Control>
                                            </Form.Group>
                                        </Card.Body>
                                        {selectedFrameColor && (
                                            <Card.Footer className="bg-light">
                                                <div className="d-flex align-items-center">
                                                    <div className={`p-2 rounded-circle me-2 border color-${selectedFrameColor.toLowerCase()}`}
                                                        style={{
                                                            width: "24px",
                                                            height: "24px"
                                                        }}>
                                                    </div>
                                                    <span>Selected: <strong>{selectedFrameColor}</strong></span>
                                                </div>
                                            </Card.Footer>
                                        )}
                                    </Card>
                                </Col>
                            </Row>

                            {estimateAccepted && colorSelectionComplete ? (
                                <div className="alert alert-success">
                                    <i className="fas fa-check-circle me-2"></i>
                                    <strong>Colors Confirmed:</strong> Your selection of <strong>{selectedMeshColor}</strong> mesh with <strong>{selectedFrameColor}</strong> frames has been saved.
                                </div>
                            ) : (
                                invoice?.status === EstimateStatus.accepted && (
                                    <div className="d-grid">
                                        <BootstrapButton
                                            className="modern-cta-button py-3 mb-3"
                                            onClick={updateColorSelections}
                                            disabled={!selectedMeshColor || !selectedFrameColor}
                                        >
                                            <i className="fas fa-save me-2"></i>
                                            Update Color Selections
                                        </BootstrapButton>
                                    </div>
                                )
                            )}
                        </Card.Body>
                    </Card>
                )}

            {/* Payment Plans Section */}
            {(!invoice?.pricingType || invoice?.pricingType === "dimensions") && invoice?.isEstimate === true && (invoice?.status !== EstimateStatus.accepted && invoice?.status !== EstimateStatus.signed) && invoice?.amount && (
                <Card className="shadow mb-4">
                    <Card.Header className="bg-primary bg-opacity-10 py-3">
                        <h4 className="m-0">
                            <i className="fas fa-credit-card me-2"></i>
                            Payment Plan Options
                        </h4>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <div className="alert alert-primary mb-4">
                            <i className="fas fa-info-circle me-2"></i>
                            Select a payment plan that works best for you. Enjoy discounts for shorter payment terms.
                        </div>

                        <div className="payment-plans-vertical">
                            <Card className="mb-4">
                                <Card.Body>
                                    <h5 className="mb-3">Choose a Payment Plan</h5>

                                    <div className="payment-plans-list">
                                        {paymentPlans.map(plan => {
                                            // Skip 12 month plan - commented out for now
                                            if (plan.id === 'months_12') {
                                                return null;
                                            }

                                            // Skip plans that have minimum amount requirements
                                            if (plan.minAmount > invoice.amount) {
                                                return null;
                                            }

                                            // Calculate payment details using the updated calculatePayment function
                                            const paymentDetails = calculatePayment(invoice.amount, plan.id);
                                            let monthlyPayment = 0;
                                            let totalAmount = paymentDetails.total;
                                            let savingsAmount = 0;

                                            if (plan.id === 'months_12') {
                                                monthlyPayment = Math.round(paymentDetails.total / 12);
                                            } else if (plan.id === 'months_6') {
                                                monthlyPayment = Math.round(paymentDetails.total / 6);
                                            } else if (plan.id === 'months_3') {
                                                monthlyPayment = Math.round(paymentDetails.total / 3);
                                            } else if (plan.id === 'half_now') {
                                                monthlyPayment = paymentDetails.initialPayment;
                                            } else if (plan.id === 'full_payment') {
                                                monthlyPayment = paymentDetails.initialPayment;
                                                totalAmount = paymentDetails.total;
                                            }

                                            savingsAmount = invoice.amount - totalAmount;

                                            return (
                                                <div key={plan.id} className={`payment-plan-option mb-3 p-3 ${selectedPaymentPlan === plan.id ? 'selected bg-light border border-primary rounded' : 'border rounded'}`}>
                                                    <div className="row align-items-center">
                                                        <div className="col-md-1">
                                                            <Form.Check
                                                                type="radio"
                                                                id={`payment-plan-${plan.id}`}
                                                                name="payment-plan"
                                                                checked={selectedPaymentPlan === plan.id}
                                                                onChange={() => setSelectedPaymentPlan(plan.id)}
                                                                className="mt-1"
                                                            />
                                                        </div>
                                                        <div className="col-md-3">
                                                            <h6 className="mb-0 fw-bold">{plan.name}</h6>
                                                            <div className="small text-muted">{plan.description}</div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="text-center">
                                                                <div className="fw-bold text-primary fs-5">
                                                                    ${monthlyPayment.toFixed(2)}
                                                                    {plan.id.startsWith('months_') && <span className="small text-muted">/month</span>}
                                                                </div>
                                                                {plan.id === 'half_now' && <div className="small text-muted">per payment</div>}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2 text-center">
                                                            {(plan.discountPercent > 0 || existingDiscountPercent > 0) && (
                                                                <div>
                                                                    {existingDiscountPercent > 0 ? (
                                                                        <span className="badge bg-success">
                                                                            {(() => {
                                                                                // Calculate combined discount
                                                                                const planDiscount = plan.discountPercent / 100;
                                                                                const existingDiscount = existingDiscountPercent / 100;
                                                                                // Use formula for combining discounts
                                                                                const combinedDiscount = planDiscount + existingDiscount - (planDiscount * existingDiscount);
                                                                                return `${Math.round(combinedDiscount * 100)}% OFF`;
                                                                            })()}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge bg-success">{plan.discountPercent}% OFF</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-md-3 text-end">
                                                            <div className="small">Total: <strong>${totalAmount.toFixed(2)}</strong></div>
                                                            <div className="small">Due Today: <strong>${monthlyPayment.toFixed(2)}</strong></div>
                                                            {savingsAmount > 0 && (
                                                                <div className="small text-success">
                                                                    Save: ${savingsAmount.toFixed(2)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-4 alert alert-info">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <i className="fas fa-info-circle fa-2x"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-1">Payment Plan Details</h6>
                                                <p className="mb-0">
                                                    For monthly plans, no interest is charged. For 50/50 payment, you pay 50% now and 50% upon completion.
                                                    For full payment, you pay the entire amount upfront and get the maximum 25% discount.
                                                    {existingDiscountPercent > 0 && ` Your existing discount of ${existingDiscountPercent}% will be combined with any payment plan discount you select.`}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Work Items from Dimensions - removed as DisplayInvoice already shows this data */}

            {/* Estimate Details */}
            {useMemo(() => {
                if (!estimateId) return null;

                // Wrap in useMemo to prevent unnecessary re-renders
                // Provide a custom setInvoice function that only updates when necessary
                const safeSetInvoice = (updatedInvoice: Invoice) => {
                    // Only update if we don't already have an invoice or if essential data has changed
                    if (!invoice || invoice.id !== updatedInvoice.id ||
                        JSON.stringify(invoice.selectedServices) !== JSON.stringify(updatedInvoice.selectedServices)) {
                        setInvoice(updatedInvoice);
                    }
                };

                return (
                    <div className="my-4">
                        <DisplayInvoice
                            key={`${estimateId}-${displayInvoiceRefreshKey}`}
                            invoiceId={estimateId}
                            setInvoice={safeSetInvoice}
                            totalDue={totalDue}
                            forceShowSignedDownload={invoice?.status === EstimateStatus.signed}
                        />
                    </div>
                );
            }, [estimateId, totalDue])}

            {/* Advanced Estimate Sections */}
            {invoice?.advancedSections && (
                <AdvancedSectionsDisplay advancedSections={invoice.advancedSections} />
            )}

            {/* E-Signature Section */}
            {invoice?.isEstimate === true && invoice?.status !== EstimateStatus.accepted && invoice?.status !== EstimateStatus.signed && (
                <div className="my-4">
                    <EstimateSignatureSection
                        estimateId={estimateId || ''}
                        customerName={invoice?.customerName || invoice?.fullName || ''}
                        onSigned={async (signerName, signedAt) => {
                            setEstimateAccepted(true);
                            if (invoice) {
                                // Calculate and save payment plan details for dimension-based estimates
                                const shouldSavePaymentPlan = !invoice.pricingType || invoice.pricingType === "dimensions";
                                let paymentPlanFields: Partial<Invoice> = {};

                                if (shouldSavePaymentPlan) {
                                    let totalAmount = invoice.amount;
                                    if (invoice.selectedServices) {
                                        const rawTotal = Object.values(invoice.selectedServices).reduce((sum, service: any) => {
                                            const price = typeof service.price === 'number' ? service.price : 0;
                                            return sum + price;
                                        }, 0) as number;
                                        if (rawTotal > 0) totalAmount = rawTotal;
                                    }

                                    const payment = calculatePayment(totalAmount, selectedPaymentPlan);

                                    // Generate deposit link
                                    let newDepositURL = `/payment/${estimateId}?amount=${payment.initialPayment}&isDeposit=true&plan=${selectedPaymentPlan}`;
                                    if (connectedAccountId) {
                                        newDepositURL += `&connectedAccountId=${connectedAccountId}`;
                                    } else if (companyId) {
                                        newDepositURL += `&companyId=${companyId}`;
                                    }

                                    paymentPlanFields = {
                                        paymentPlan: selectedPaymentPlan as Invoice['paymentPlan'],
                                        discountedTotal: payment.total,
                                        discountPercent: payment.discount * 100,
                                        depositAmount: payment.initialPayment,
                                        depositLink: newDepositURL
                                    };

                                    setDepositURL(newDepositURL);
                                }

                                // Save payment plan details to Firestore
                                if (Object.keys(paymentPlanFields).length > 0) {
                                    try {
                                        const docRef = doc(db, getCollectionName("estimates"), invoice.id);
                                        await updateDoc(docRef, paymentPlanFields);
                                    } catch (error) {
                                        console.error("Error saving payment plan details:", error);
                                    }
                                }

                                setInvoice({
                                    ...invoice,
                                    ...paymentPlanFields,
                                    status: EstimateStatus.signed,
                                    signatureLocked: true,
                                    signature: {
                                        signerFullName: signerName,
                                        signedAtUTC: signedAt,
                                        signatureImagePath: '',
                                        ipAddress: '',
                                        userAgent: '',
                                        documentHash: '',
                                        consentGiven: true,
                                        consentText: '',
                                        signedPDFPath: ''
                                    }
                                });
                            }
                            // Force DisplayInvoice to remount/refetch so signed UI updates immediately.
                            setDisplayInvoiceRefreshKey((prev) => prev + 1);
                        }}
                    />
                </div>
            )}

            {/* Pay Deposit Button */}
            {invoice?.isEstimate === true && (invoice?.status === EstimateStatus.accepted || invoice?.status === EstimateStatus.signed) && invoice?.paymentPlan && invoice?.paymentPlan !== "none" && (
                <Card className="shadow mb-5">
                    <Card.Header className="bg-success bg-opacity-10 py-3">
                        <h4 className="m-0 text-success">
                            <i className="fas fa-check-circle me-2"></i>
                            Next Steps
                        </h4>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <div className="alert alert-success mb-4">
                            <div className="d-flex">
                                <div className="me-3">
                                    <i className="fas fa-check-circle fa-2x"></i>
                                </div>
                                <div>
                                    <h5 className="mb-2">{invoice?.status === EstimateStatus.signed ? 'Estimate Signed!' : 'Estimate Accepted!'}</h5>
                                    <p className="mb-0">
                                        Thank you for {invoice?.status === EstimateStatus.signed ? 'signing' : 'accepting'} your {(!invoice?.pricingType || invoice?.pricingType === "dimensions") ? "solar screen" : ""} estimate. To proceed with installation, please make your
                                        initial payment of <strong>${invoice.paymentPlan === 'half_now' ? ((invoice.discountedTotal ?? invoice.amount) / 2).toFixed(2) : (invoice.depositAmount || invoice.amount * 0.5).toFixed(2)}</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Selected Payment Plan Summary - Only show for dimension-based pricing or undefined pricingType */}
                        {(!invoice?.pricingType || invoice?.pricingType === "dimensions") && (
                            <div className="bg-light p-3 rounded-lg mb-4">
                                <h5 className="border-bottom pb-2 mb-3">Your Selected Payment Plan</h5>

                                {invoice.paymentPlan === 'months_12' && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col-md-8">
                                            <h6 className="mb-1">12-Month Payment Plan</h6>
                                            <p className="mb-0 text-muted">No interest, equal monthly payments</p>
                                            <ul className="mt-2 mb-0">
                                                <li>Monthly payment: <strong>${((invoice.discountedTotal ?? invoice.amount) / 12).toFixed(2)}</strong></li>
                                                <li>Total price: <strong>${(invoice.discountedTotal ?? invoice.amount).toFixed(2)}</strong></li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4 text-center">
                                            <div className="display-6 text-primary">${((invoice.discountedTotal ?? invoice.amount) / 12).toFixed(2)}</div>
                                            <div className="small text-muted">per month</div>
                                        </div>
                                    </div>
                                )}

                                {invoice.paymentPlan === 'months_6' && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col-md-8">
                                            <h6 className="mb-1">6-Month Payment Plan</h6>
                                            <p className="mb-0 text-muted">No interest, equal monthly payments</p>
                                            {(invoice.discountPercent ?? 0) > 0 && (
                                                <p className="mb-0 text-success">Includes <strong>{invoice.discountPercent}% discount</strong></p>
                                            )}
                                            <ul className="mt-2 mb-0">
                                                <li>Monthly payment: <strong>${((invoice.discountedTotal ?? 0) / 6).toFixed(2)}</strong></li>
                                                <li>Total price: <strong>${(invoice.discountedTotal ?? 0).toFixed(2)}</strong> (was ${(invoice.amount ?? 0).toFixed(2)})</li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4 text-center">
                                            <div className="display-6 text-primary">${((invoice.discountedTotal ?? 0) / 6).toFixed(2)}</div>
                                            <div className="small text-muted">per month</div>
                                        </div>
                                    </div>
                                )}

                                {invoice.paymentPlan === 'months_3' && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col-md-8">
                                            <h6 className="mb-1">3-Month Payment Plan</h6>
                                            <p className="mb-0 text-muted">No interest, equal monthly payments</p>
                                            {(invoice.discountPercent ?? 0) > 0 && (
                                                <p className="mb-0 text-success">Includes <strong>{Math.round(invoice.discountPercent ?? 0)}% discount</strong></p>
                                            )}
                                            <ul className="mt-2 mb-0">
                                                <li>Monthly payment: <strong>${((invoice.discountedTotal ?? 0) / 3).toFixed(2)}</strong></li>
                                                <li>Total price: <strong>${(invoice.discountedTotal ?? 0).toFixed(2)}</strong> (was ${(invoice.amount ?? 0).toFixed(2)})</li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4 text-center">
                                            <div className="display-6 text-primary">${((invoice.discountedTotal ?? 0) / 3).toFixed(2)}</div>
                                            <div className="small text-muted">per month</div>
                                        </div>
                                    </div>
                                )}

                                {invoice.paymentPlan === 'half_now' && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col-md-8">
                                            <h6 className="mb-1">Half Now, Half After Installation</h6>
                                            {(invoice.discountPercent ?? 0) > 0 && (
                                                <p className="mb-0 text-success">Includes <strong>{Math.round(invoice.discountPercent ?? 0)}% discount</strong></p>
                                            )}
                                            <ul className="mt-2 mb-0">
                                                <li>First payment: <strong>${((invoice.discountedTotal ?? 0) / 2).toFixed(2)}</strong></li>
                                                <li>Final payment: <strong>${((invoice.discountedTotal ?? 0) / 2).toFixed(2)}</strong> (due after installation is completed)</li>
                                                <li>Total price: <strong>${(invoice.discountedTotal ?? 0).toFixed(2)}</strong> (was ${(invoice.amount ?? 0).toFixed(2)})</li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4 text-center">
                                            <div className="display-6 text-primary">${((invoice.discountedTotal ?? 0) / 2).toFixed(2)}</div>
                                            <div className="small text-muted">now</div>
                                        </div>
                                    </div>
                                )}

                                {invoice.paymentPlan === 'full_payment' && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col-md-8">
                                            <h6 className="mb-1">Full Payment</h6>
                                            {(invoice.discountPercent ?? 0) > 0 && (
                                                <p className="mb-0 text-success">Includes <strong>{Math.round(invoice.discountPercent ?? 0)}% discount</strong></p>
                                            )}
                                            <ul className="mt-2 mb-0">
                                                <li>Full payment: <strong>${(invoice.depositAmount ?? 0).toFixed(2)}</strong></li>
                                                <li>Total price: <strong>${(invoice.discountedTotal ?? 0).toFixed(2)}</strong> (was ${(invoice.amount ?? 0).toFixed(2)})</li>
                                                <li>Your savings: <strong>${((invoice.amount ?? 0) - (invoice.discountedTotal ?? 0)).toFixed(2)}</strong></li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4 text-center">
                                            <div className="display-6 text-primary">${(invoice.depositAmount ?? 0).toFixed(2)}</div>
                                            <div className="small text-muted">one-time payment</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="bg-light p-3 rounded-lg mb-4">
                            <h5 className="border-bottom pb-2 mb-3">What Happens Next?</h5>
                            <ol className="mb-0">
                                <li className="mb-2">Make your initial payment to secure your installation appointment</li>
                                <li className="mb-2">Our team will contact you within 1-2 business days to schedule installation</li>
                                <li className="mb-2">Installation is typically completed within 2-3 weeks</li>
                                {(!invoice?.pricingType || invoice?.pricingType === "dimensions") && invoice.paymentPlan === 'half_now' ? (
                                    <li>The remaining balance will be due upon completion of installation</li>
                                ) : (!invoice?.pricingType || invoice?.pricingType === "dimensions") ? (
                                    <li>Remaining payments will be processed according to your selected plan</li>
                                ) : (
                                    <li>Payment will be processed according to your agreement</li>
                                )}
                            </ol>
                        </div>

                        <BootstrapButton
                            className="modern-cta-button w-100 py-3 bg-success border-success"
                            onClick={() => {
                                if (invoice.firstInvoiceId) {
                                    const url = `/invoice/${invoice.firstInvoiceId}${connectedAccountId ? `?connectedAccountId=${connectedAccountId}` : (companyId ? `?companyId=${companyId}` : '')}`;
                                    navigate(url);
                                } else {
                                    window.location.href = invoice.depositLink || depositURL;
                                }
                            }}
                        >
                            <i className="fas fa-credit-card me-2"></i>
                            Make Payment of ${invoice.paymentPlan === 'half_now' ? ((invoice.discountedTotal ?? 0) / 2).toFixed(2) : (invoice.depositAmount ?? 0).toFixed(2)}
                        </BootstrapButton>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default Estimate;
